import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useSessionStore, waitForHydration } from '@/stores/sessionStore'
import { useSelectStore, useSwitchStore, useAccessibleStores, useLogout } from '@/api/auth'
import { Building2, ChevronLeft, Leaf, Check, AlertCircle, Search } from 'lucide-react'
import { AuthUserBadge } from '@/components/AuthUserBadge'
import type { Store } from '@/types'

export const Route = createFileRoute('/select-store')({
  beforeLoad: async () => {
    await waitForHydration()
    const { backendUrl, sessionToken, userToken } = useSessionStore.getState()
    if (!backendUrl) {
      throw redirect({ to: '/connect' })
    }
    // Allow either a manager mid-pick (user_token only) or a completed
    // session (session_token, e.g. API-key flow on its way to
    // /select-register). With neither, the user isn't authenticated.
    if (!sessionToken && !userToken) {
      throw redirect({ to: '/auth' })
    }
  },
  component: SelectStorePage,
})

function SelectStorePage() {
  const navigate = useNavigate()
  const sessionToken = useSessionStore((s) => s.sessionToken)
  const userToken = useSessionStore((s) => s.userToken)
  const authType = useSessionStore((s) => s.authType)
  const cachedStores = useSessionStore((s) => s.accessibleStores)
  const selectedStore = useSessionStore((s) => s.selectedStore)

  const selectStore = useSelectStore()
  const switchStore = useSwitchStore()
  const logoutMutation = useLogout()
  // Stale-while-revalidate: every mount refetches /v2/auth/stores so an
  // admin revoking access mid-session shows up here, but we render the
  // cached list immediately to avoid a spinner on the common case.
  const { data: refreshedStores, isFetching: storesLoading, error: storesError } =
    useAccessibleStores(authType === 'manager')
  const stores = refreshedStores ?? cachedStores ?? []

  // Persist the fresh list back into the session store so a hard
  // reload starts from current data instead of last-login data.
  // `setAccessibleStores` is a no-op identity write when the BE
  // returned the same list, so this won't churn React.
  const setAccessibleStores = useSessionStore((s) => s.setAccessibleStores)
  useEffect(() => {
    if (!refreshedStores) return
    setAccessibleStores(refreshedStores)
  }, [refreshedStores, setAccessibleStores])

  // Track which store is currently being exchanged (auto-pick or
  // user-clicked) so the picker grid can disable + show a spinner on
  // that card without blocking the rest of the UI.
  const [pendingStoreId, setPendingStoreId] = useState<number | null>(null)
  const [pickError, setPickError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // API-key (store) auth is register-bound — no picker, /v2/auth/switch-store
  // would 403. Bounce straight to register selection.
  useEffect(() => {
    if (authType === 'store' && sessionToken && selectedStore) {
      navigate({ to: '/select-register', replace: true })
    }
  }, [authType, sessionToken, selectedStore, navigate])

  // Auto-pick when the manager has access to exactly one store —
  // there's no choice to confirm, so skip the picker on every path:
  //   • Initial login (userToken):  /v2/auth/select-store, then /select-register
  //   • Re-pick, same store:        /select-register (already authed there)
  //   • Re-pick, different store:   /v2/auth/switch-store, then /select-register
  const autoPickedRef = useRef(false)
  useEffect(() => {
    if (autoPickedRef.current) return
    if (stores.length !== 1) return
    autoPickedRef.current = true
    const only = stores[0]

    // Re-pick path where the only accessible store is already the one
    // we're scoped to — no need to call the BE, just continue.
    if (!userToken && selectedStore?.id === only.id) {
      navigate({ to: '/select-register', replace: true })
      return
    }

    setPendingStoreId(only.id)
    setPickError(null)
    const mutation = userToken ? selectStore : switchStore
    mutation.mutate(
      { store: only },
      {
        onSuccess: () => {
          navigate({ to: '/select-register', replace: true })
        },
        onError: (err) => {
          setPendingStoreId(null)
          setPickError(err instanceof Error ? err.message : 'Could not select store.')
          autoPickedRef.current = false
        },
      },
    )
  }, [userToken, stores, selectStore, switchStore, selectedStore, navigate])

  const handlePick = (store: Store) => {
    if (pendingStoreId !== null) return
    setPendingStoreId(store.id)
    setPickError(null)

    // Initial pick exchanges user_token; re-pick swaps the
    // session_token via /v2/auth/switch-store. Same response shape,
    // same destination on success.
    const mutation = userToken ? selectStore : switchStore
    mutation.mutate(
      { store },
      {
        onSuccess: () => {
          navigate({ to: '/select-register' })
        },
        onError: (err) => {
          setPendingStoreId(null)
          setPickError(err instanceof Error ? err.message : 'Could not select store.')
        },
      },
    )
  }

  const handleBack = () => {
    // Initial-pick path: only userToken — back means abandon login.
    // Re-pick path: there's a live session — back returns to the
    // store/register the cashier was already on. The "abandon login"
    // path goes through useLogout('full') so the react-query cache is
    // wiped (otherwise a stale `accessible_stores: []` from the prior
    // session would shadow the next login's data).
    if (userToken && !sessionToken) {
      logoutMutation.mutate('full', {
        onSettled: () => navigate({ to: '/auth' }),
      })
      return
    }
    if (selectedStore) {
      navigate({ to: '/select-register' })
      return
    }
    logoutMutation.mutate('full', {
      onSettled: () => navigate({ to: '/auth' }),
    })
  }

  // Render decision. Manager flow shows the picker; the API-key
  // bounce above handles store sessions before this point.
  const inManagerFlow = authType === 'manager'

  // Sort the picker A-Z by name so the grid is scannable instead of
  // following the backend's return order.
  const sortedStores = useMemo(
    () =>
      [...stores].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
      ),
    [stores],
  )

  // Filter the (already sorted) list by the search box, matching the
  // same fields the placeholder advertises: name, city, province.
  const filteredStores = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sortedStores
    return sortedStores.filter((store) =>
      [store.name, store.city, store.province]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(q)),
    )
  }, [sortedStores, search])

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">WAIO POS</h1>
              <p className="text-sm text-gray-500">Select Store</p>
            </div>
          </div>
          <AuthUserBadge />
        </div>
      </header>

      <main className="flex-1 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
              <Building2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Select a Store</h2>
            <p className="text-gray-500 mt-2">Choose which store you'll be working at</p>
          </div>

          {pickError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Could not select store</p>
                <p className="text-sm text-red-600">{pickError}</p>
              </div>
              <button
                onClick={() => setPickError(null)}
                className="text-red-600 hover:text-red-700 text-sm font-medium cursor-pointer"
              >
                Dismiss
              </button>
            </div>
          )}

          {!inManagerFlow ? (
            // API-key flow: the navigate effect above is mid-flight.
            <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
          ) : storesLoading && stores.length === 0 ? (
            // Spinner only when we have nothing to render — if a cached
            // list exists, the background revalidation is silent.
            <div className="text-center py-12 text-gray-500 text-sm">Loading stores…</div>
          ) : storesError && stores.length === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-red-900 mb-1">Failed to load stores</h3>
              <p className="text-sm text-red-600">
                {storesError instanceof Error ? storesError.message : 'Unable to connect to server'}
              </p>
            </div>
          ) : stores.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="font-semibold text-amber-900 mb-1">No store access</h3>
              <p className="text-sm text-amber-700 mb-4">
                Your account doesn't have access to any stores yet. Ask an admin to grant store access.
              </p>
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium cursor-pointer"
              >
                Back to Sign In
              </button>
            </div>
          ) : stores.length === 1 ? (
            // Auto-pick is in flight (every path — initial login or
            // re-pick) — keep the page from looking blank.
            <div className="text-center py-12 text-gray-500 text-sm">Selecting store…</div>
          ) : (
            <>
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search stores by name, city, or province"
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {filteredStores.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                  No stores match “{search.trim()}”
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {filteredStores.map((store) => {
                    const isSelected = selectedStore?.id === store.id
                    const isPending = pendingStoreId === store.id
                    const subtitle =
                      [store.city, store.province].filter(Boolean).join(', ')
                      || store.address
                      || `Store ${store.id}`
                    return (
                      <button
                        key={store.id}
                        onClick={() => handlePick(store)}
                        disabled={pendingStoreId !== null}
                        className={`relative bg-white rounded-xl p-4 flex flex-col items-start text-left transition-all hover:ring-2 hover:ring-green-500 hover:ring-offset-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:ring-0 ${
                          isSelected ? 'ring-2 ring-green-500 ring-offset-2' : ''
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-green-100">
                          {isPending ? (
                            <div className="w-5 h-5 border-2 border-green-500/30 border-t-green-600 rounded-full animate-spin" />
                          ) : (
                            <Building2 className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{store.name || `Store ${store.id}`}</h3>
                        <p className="text-sm text-gray-500 mt-0.5 truncate w-full">{subtitle}</p>
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}
